/**
 * MarketPage.js — рынок L2 Harbor
 * Группировка, accordion + recharts (WTS/WTB), % изменения, фильтры
 */
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { supabase } from '../api/supabase';

const fmt   = n => Number(n || 0).toLocaleString('ru-RU');
const fmtK  = n => {
  if (!n) return '—';
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000)    return (n / 1000).toFixed(0) + 'K';
  return String(n);
};

const CITIES = ['Все', 'Gludin', 'Gludio', 'Dion', 'Giran', 'Aden'];
const CITY_COLORS = {
  Gludin: '#60a5fa', Gludio: '#34d399', Dion: '#fbbf24',
  Giran:  '#a78bfa', Aden:   '#f87171',
};
const RANGE_OPTIONS = [
  { value: 7,  label: '7д' },
  { value: 14, label: '14д' },
  { value: 21, label: '21д' },
  { value: 30, label: '30д' },
  { value: 0,  label: 'Всё' },
];
const COLOR_SELL = '#60a5fa';
const COLOR_BUY  = '#fbbf24';

// ── Хук сортировки ────────────────────────────────────────────────────────────
function useSort(defaultCol, defaultDir = 'asc') {
  const [col, setCol] = useState(defaultCol);
  const [dir, setDir] = useState(defaultDir);
  const toggle = c => {
    if (c === col) setDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setCol(c); setDir('asc'); }
  };
  const apply = (arr, getters) => {
    const g = getters[col];
    if (!g) return arr;
    return [...arr].sort((a, b) => {
      const av = g(a), bv = g(b);
      const cmp = typeof av === 'number' && typeof bv === 'number'
        ? av - bv
        : String(av ?? '').localeCompare(String(bv ?? ''), 'ru');
      return dir === 'asc' ? cmp : -cmp;
    });
  };
  return { col, dir, toggle, apply };
}

// ── Заголовок с сортировкой ───────────────────────────────────────────────────
function SortTh({ id, sort, children, style = {} }) {
  const active = sort.col === id;
  return (
    <span
      onClick={() => sort.toggle(id)}
      style={{
        cursor: 'pointer', userSelect: 'none',
        color: active ? 'var(--blue2)' : 'var(--muted)',
        display: 'flex', alignItems: 'center', gap: 3,
        justifyContent: style.textAlign === 'right' ? 'flex-end' : style.textAlign === 'center' ? 'center' : 'flex-start',
        ...style,
      }}
    >
      {children}
      <span style={{ fontSize: 9, opacity: active ? 1 : 0.3 }}>
        {active ? (sort.dir === 'asc' ? '▲' : '▼') : '⇅'}
      </span>
    </span>
  );
}

// ── Кастомный Tooltip для графика ────────────────────────────────────────────
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'var(--panel)', border: '1px solid var(--border2)',
      borderRadius: 4, padding: '8px 12px',
      fontFamily: 'JetBrains Mono,monospace', fontSize: 11,
    }}>
      <div style={{ color: 'var(--muted)', marginBottom: 4 }}>{label}</div>
      {payload.map(p => (
        <div key={p.name} style={{ color: p.color }}>
          {p.name}: {fmt(p.value)} ₳
        </div>
      ))}
    </div>
  );
}

// ── Компонент графика ─────────────────────────────────────────────────────────
function PriceChart({ itemName, range }) {  const [data, setData]     = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!itemName) return;
    setLoading(true);
    let query = supabase
      .from('price_history')
      .select('date, mode, min_price')
      .eq('item_name', itemName)
      .order('date', { ascending: true });

    if (range > 0) {
      const since = new Date();
      since.setDate(since.getDate() - range);
      query = query.gte('date', since.toISOString().split('T')[0]);
    }

    query.then(({ data: rows, error }) => {
      if (error) { setData([]); setLoading(false); return; }
      // Сводим SELL и BUY по дате в один объект
      const byDate = {};
      (rows || []).forEach(r => {
        if (!byDate[r.date]) byDate[r.date] = { date: r.date };
        if (r.mode === 'SELL') byDate[r.date].sell = r.min_price;
        if (r.mode === 'BUY')  byDate[r.date].buy  = r.min_price;
      });
      setData(Object.values(byDate));
      setLoading(false);
    });
  }, [itemName, range]);

  if (loading) return (
    <div style={{ textAlign: 'center', color: 'var(--muted)', padding: 20, fontSize: 12, fontFamily: 'Rajdhani,sans-serif' }}>
      Загрузка графика...
    </div>
  );
  if (!data.length) return (
    <div style={{ textAlign: 'center', color: 'var(--muted)', padding: 20, fontSize: 12, fontFamily: 'Rajdhani,sans-serif' }}>
      Недостаточно данных для графика
    </div>
  );

  return (
    <ResponsiveContainer width="100%" height={180}>
      <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.05)" />
        <XAxis
          dataKey="date"
          tick={{ fill: 'var(--muted)', fontSize: 10, fontFamily: 'JetBrains Mono,monospace' }}
          tickFormatter={d => d.slice(5)}  // MM-DD
          tickLine={false} axisLine={false}
        />
        <YAxis
          tick={{ fill: 'var(--muted)', fontSize: 10, fontFamily: 'JetBrains Mono,monospace' }}
          tickFormatter={fmtK}
          tickLine={false} axisLine={false} width={44}
        />
        <Tooltip content={<ChartTooltip />} />
        <Legend
          wrapperStyle={{ fontSize: 11, fontFamily: 'Rajdhani,sans-serif', paddingTop: 4 }}
          formatter={(v) => v === 'sell' ? 'WTS (Продажа)' : 'WTB (Покупка)'}
        />
        <Line
          type="monotone" dataKey="sell" name="sell"
          stroke={COLOR_SELL} strokeWidth={2}
          dot={{ r: 3, fill: COLOR_SELL }}
          activeDot={{ r: 5 }}
        />
        <Line
          type="monotone" dataKey="buy" name="buy"
          stroke={COLOR_BUY} strokeWidth={2}
          dot={{ r: 3, fill: COLOR_BUY }}
          activeDot={{ r: 5 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

// ── График истории спреда арбитража (WTB max − WTS min по дням) ──────────────
function SpreadChart({ itemName }) {
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!itemName) return;
    setLoading(true);
    supabase
      .from('price_history')
      .select('date, mode, min_price, max_price')
      .eq('item_name', itemName)
      .order('date', { ascending: true })
      .then(({ data: rows, error }) => {
        if (error) { setData([]); setLoading(false); return; }
        const byDate = {};
        (rows || []).forEach(r => {
          if (!byDate[r.date]) byDate[r.date] = { date: r.date };
          if (r.mode === 'SELL') byDate[r.date].sell = r.min_price;
          if (r.mode === 'BUY')  byDate[r.date].buy  = r.max_price ?? r.min_price;
        });
        setData(
          Object.values(byDate).map(d => ({
            ...d,
            spread: d.buy != null && d.sell != null ? d.buy - d.sell : null,
          }))
        );
        setLoading(false);
      });
  }, [itemName]);

  if (loading) return (
    <div style={{ textAlign: 'center', color: 'var(--muted)', padding: 20, fontSize: 12, fontFamily: 'Rajdhani,sans-serif' }}>
      Загрузка графика...
    </div>
  );
  if (!data.length) return (
    <div style={{ textAlign: 'center', color: 'var(--muted)', padding: 20, fontSize: 12, fontFamily: 'Rajdhani,sans-serif' }}>
      Недостаточно данных для графика
    </div>
  );

  return (
    <ResponsiveContainer width="100%" height={140}>
      <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.05)" />
        <XAxis
          dataKey="date"
          tick={{ fill: 'var(--muted)', fontSize: 10, fontFamily: 'JetBrains Mono,monospace' }}
          tickFormatter={d => d.slice(5)}
          tickLine={false} axisLine={false}
        />
        <YAxis
          tick={{ fill: 'var(--muted)', fontSize: 10, fontFamily: 'JetBrains Mono,monospace' }}
          tickFormatter={fmtK}
          tickLine={false} axisLine={false} width={44}
        />
        <Tooltip content={<ChartTooltip />} />
        <Line
          type="monotone" dataKey="spread" name="spread"
          stroke="var(--green)" strokeWidth={2}
          dot={{ r: 3, fill: 'var(--green)' }}
          activeDot={{ r: 5 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

// ── Хук: загрузка % изменения цены относительно вчера ────────────────────────
function usePriceChanges(mode) {
  const [changes, setChanges] = useState({});

  useEffect(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yStr = yesterday.toISOString().split('T')[0];

    supabase
      .from('price_history')
      .select('item_name, min_price, avg_price')
      .eq('mode', mode)
      .eq('date', yStr)
      .then(({ data }) => {
        const map = {};
        (data || []).forEach(r => { map[r.item_name] = r; });
        setChanges(map);
      });
  }, [mode]);

  return changes;
}

// ── Тег города ───────────────────────────────────────────────────────────────
function CityTag({ city }) {
  if (!city) return <span style={{ color: 'var(--muted)', fontSize: 11 }}>—</span>;
  const color = CITY_COLORS[city] || '#94a3b8';
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 2,
      fontSize: 11, fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, letterSpacing: 1,
      color, background: color + '18', border: `1px solid ${color}40`,
    }}>{city}</span>
  );
}

// ── Экран входа ──────────────────────────────────────────────────────────────
const MARKET_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD;

function LoginScreen({ onAuth }) {
  const [pwInput, setPwInput] = useState('');
  const [pwError, setPwError] = useState(false);

  const handleLogin = () => {
    if (pwInput === MARKET_PASSWORD) {
      sessionStorage.setItem('marketAuth', '1');
      onAuth();
    } else {
      setPwError(true);
      setPwInput('');
    }
  };

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 320 }}>
        <div style={{ fontFamily: 'Rajdhani,sans-serif', fontSize: 24, fontWeight: 700, color: 'var(--blue3)', letterSpacing: 3, textTransform: 'uppercase', textAlign: 'center', marginBottom: 24 }}>
          📦 Рынок L2 Harbor
        </div>
        <div className="pane">
          <div className="pane-head">🔐 Доступ</div>
          <div className="pane-body">
            <div className="admin-form">
              <input
                type="password"
                placeholder="Пароль"
                value={pwInput}
                onChange={e => { setPwInput(e.target.value); setPwError(false); }}
                onKeyDown={e => e.key === 'Enter' && handleLogin()}
                style={{ borderColor: pwError ? 'var(--red)' : '' }}
                autoFocus
              />
              {pwError && <div style={{ color: 'var(--red)', fontSize: 12, fontFamily: 'Rajdhani,sans-serif' }}>Неверный пароль</div>}
              <button onClick={handleLogin}>ВОЙТИ</button>
            </div>
          </div>
        </div>
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <a href="/" style={{ fontFamily: 'Rajdhani,sans-serif', fontSize: 12, color: 'var(--muted)', textDecoration: 'none' }}>← На главную</a>
        </div>
      </div>
    </div>
  );
}

// ── Обёртка с авторизацией ────────────────────────────────────────────────────
export function MarketPage() {
  const [auth, setAuth] = useState(() => sessionStorage.getItem('marketAuth') === '1');
  if (!auth) return <LoginScreen onAuth={() => setAuth(true)} />;
  return <MarketInner />;
}

// ── Основной компонент ────────────────────────────────────────────────────────
function MarketInner() {
  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [msg, setMsg]             = useState(null);
  const [search, setSearch]       = useState('');
  const [city, setCity]           = useState('Все');
  const [tab, setTab]             = useState('SELL'); // SELL | BUY | ARB
  const mode = tab === 'ARB' ? 'SELL' : tab;
  const [expanded, setExpanded]   = useState(null);
  const [chartRange, setChartRange] = useState(7);
  const [uploading, setUploading] = useState(false);
  const [uploadAuth, setUploadAuth] = useState(false);
  const [scanDate, setScanDate]     = useState('latest');
  const [minSpread, setMinSpread]   = useState(0); // арбитраж: мин. спред в ₳ (0 = без фильтра)
  const fileRef = useRef();

  // Сортировка главной таблицы — по умолчанию по цене
  const groupSort = useSort('best_price', 'asc');
  // Сортировка accordion (офферов внутри предмета) — по цене
  const entrySort = useSort('price', 'asc');

  const yesterdayChanges = usePriceChanges(mode);

  const showMsg = (text, ok = true) => {
    setMsg({ text, ok });
    setTimeout(() => setMsg(null), 4000);
  };

  // ── Загрузка текущих данных ───────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('market_prices')
      .select('*')
      .order('created_at', { ascending: false });
    if (!error) setRows(data || []);
    else showMsg('✗ Ошибка загрузки: ' + error.message, false);
    setLoading(false);
  }, []);

  // ── Уникальные даты сканирования ──────────────────────────────────────────
  const scanDates = useMemo(() => {
    const dates = new Set();
    rows.forEach(r => {
      if (r.scanned_at) {
        const d = r.scanned_at.split(' ')[0]; // "2026-03-19 20:31" → "2026-03-19"
        if (/^\d{4}-\d{2}-\d{2}$/.test(d)) dates.add(d);
      }
    });
    return [...dates].sort().reverse(); // от новых к старым
  }, [rows]);

  useEffect(() => { load(); }, [load]);

  // ── Загрузка JSON ─────────────────────────────────────────────────────────
  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    showMsg('⏳ Обрабатываем файл...');
    try {
      const json = JSON.parse(await file.text());
      const inserts = [];
      for (const [item_name, entries] of Object.entries(json)) {
        if (!Array.isArray(entries)) continue;
        for (const en of entries) {
          inserts.push({
            item_name,
            trader:     en.trader || null,
            price:      Number(en.price) || 0,
            count:      Number(en.count) || 1,
            mode:       (en.mode || 'SELL').toUpperCase(),
            city:       en.city   || null,
            scanned_at: en.time   || null,
          });
        }
      }
      if (!inserts.length) { showMsg('✗ Файл пустой', false); setUploading(false); return; }

      // Вставляем данные батчами по 500 (без удаления старых — всё хранится)
      let total = 0;
      for (let i = 0; i < inserts.length; i += 500) {
        const { error } = await supabase.from('market_prices').insert(inserts.slice(i, i + 500));
        if (error) { showMsg('✗ ' + error.message, false); setUploading(false); return; }
        total += Math.min(500, inserts.length - i);
      }

      // После загрузки — вызываем агрегацию в историю
      await supabase.rpc('aggregate_price_history');

      showMsg(`✓ Загружено ${total} записей`);
      setScanDate('latest'); // показываем только что загруженные данные
      await load();
    } catch (err) {
      showMsg('✗ Ошибка парсинга: ' + err.message, false);
    }
    setUploading(false);
    e.target.value = '';
  };

  // ── Группировка ───────────────────────────────────────────────────────────
  const groups = useMemo(() => {
    const q = search.toLowerCase().trim();
    const effectiveDate = scanDate === 'latest' ? scanDates[0] : scanDate;
    const filtered = rows.filter(r => {
      if ((r.mode || 'SELL').toUpperCase() !== mode) return false;
      if (city !== 'Все' && r.city !== city) return false;
      if (q && !r.item_name?.toLowerCase().includes(q)) return false;
      // Фильтр по дате сканирования
      if (effectiveDate && r.scanned_at) {
        const rowDate = r.scanned_at.split(' ')[0];
        if (rowDate !== effectiveDate) return false;
      }
      return true;
    });

    const map = {};
    filtered.forEach(r => {
      if (!map[r.item_name]) map[r.item_name] = { item_name: r.item_name, total_count: 0, entries: [] };
      map[r.item_name].total_count += Number(r.count) || 0;
      map[r.item_name].entries.push(r);
    });

    const rawGroups = Object.values(map).map(g => {
      const prices = g.entries.map(e => e.price).filter(p => p > 0);
      const best   = prices.length
        ? (mode === 'SELL' ? Math.min(...prices) : Math.max(...prices))
        : 0;

      // % изменения относительно вчера
      const yest = yesterdayChanges[g.item_name];
      let pct = null;
      if (yest?.avg_price && best) {
        pct = ((best - yest.avg_price) / yest.avg_price * 100);
      }

      return { ...g, best_price: best, pct };
    });

    // Применяем выбранную сортировку
    return groupSort.apply(rawGroups, {
      item_name:   g => g.item_name,
      total_count: g => g.total_count,
      best_price:  g => g.best_price || (mode === 'SELL' ? Infinity : 0),
      pct:         g => g.pct ?? -999,
      offers:      g => g.entries.length,
    });
  }, [rows, mode, city, search, scanDate, scanDates, yesterdayChanges, groupSort]);

  const toggle = name => setExpanded(prev => prev === name ? null : name);

  // ── Арбитраж: постоянной табы, предметы где WTB дороже WTS ─────────────────
  const arbSort = useSort('spreadPct', 'desc');

  const arbDeals = useMemo(() => {
    if (tab !== 'ARB') return [];
    const effectiveDate = scanDate === 'latest' ? scanDates[0] : scanDate;
    const q = search.toLowerCase().trim();
    const filtered = rows.filter(r => {
      if (city !== 'Все' && r.city !== city) return false;
      if (q && !r.item_name?.toLowerCase().includes(q)) return false;
      if (effectiveDate && r.scanned_at) {
        const rowDate = r.scanned_at.split(' ')[0];
        if (rowDate !== effectiveDate) return false;
      }
      return true;
    });

    const map = {};
    filtered.forEach(r => {
      if (!map[r.item_name]) map[r.item_name] = { item_name: r.item_name, sells: [], buys: [] };
      const arr = (r.mode || 'SELL').toUpperCase() === 'SELL' ? 'sells' : 'buys';
      map[r.item_name][arr].push(r);
    });

    const deals = [];
    for (const g of Object.values(map)) {
      const sells = g.sells.filter(e => e.price > 0);
      const buys  = g.buys.filter(e => e.price > 0);
      if (!sells.length || !buys.length) continue;

      const bestSellPrice = Math.min(...sells.map(e => e.price));
      const bestBuyPrice  = Math.max(...buys.map(e => e.price));
      if (bestBuyPrice <= bestSellPrice) continue;

      const spread    = bestBuyPrice - bestSellPrice;
      if (minSpread > 0 && spread < minSpread) continue;
      const spreadPct = spread / bestSellPrice * 100;

      deals.push({
        item_name: g.item_name,
        spread,
        spreadPct,
        sellers: sells
          .filter(e => e.price < bestBuyPrice)
          .sort((a, b) => a.price - b.price)
          .map(e => ({ ...e, profit: bestBuyPrice - e.price })),
        buyers: [...buys].sort((a, b) => b.price - a.price),
      });
    }

    return arbSort.apply(deals, {
      item_name: d => d.item_name,
      spread:    d => d.spread,
      spreadPct: d => d.spreadPct,
      sells:     d => d.sellers.length,
      buys:      d => d.buyers.length,
    });
  }, [tab, rows, city, search, scanDate, scanDates, minSpread, arbSort]);

  // ── Стили ─────────────────────────────────────────────────────────────────
  const priceColor = mode === 'SELL' ? COLOR_SELL : COLOR_BUY;

  const tabStyle = active => ({
    padding: '10px 28px',
    background: 'none', border: 'none',
    borderBottom: active ? `2px solid ${active === 'SELL' ? COLOR_SELL : COLOR_BUY}` : '2px solid transparent',
    color: active ? (active === 'SELL' ? COLOR_SELL : COLOR_BUY) : 'var(--muted)',
    fontFamily: 'Rajdhani,sans-serif', fontSize: 14, fontWeight: 700,
    letterSpacing: 1, cursor: 'pointer', textTransform: 'uppercase',
    transition: 'all .15s',
  });

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh', position: 'relative', zIndex: 1 }}>

      {/* ── Шапка ── */}
      <div style={{ background: 'linear-gradient(180deg,#0d1b35 0%,transparent 100%)', borderBottom: '1px solid var(--border)', paddingBottom: 0 }}>
        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '16px 16px 0' }}>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
            <div>
              <div style={{ fontFamily: 'Rajdhani,sans-serif', fontSize: 22, fontWeight: 700, color: 'var(--blue3)', letterSpacing: 2, textTransform: 'uppercase' }}>
                📦 Рынок L2 Harbor
              </div>
              <div style={{ fontFamily: 'Rajdhani,sans-serif', fontSize: 12, color: 'var(--muted)', letterSpacing: 1, marginTop: 2 }}>
                Eternal C1 x1 · цены магазинов по городам
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <a href="/craft" style={{ fontFamily: 'Rajdhani,sans-serif', fontSize: 12, color: 'var(--blue2)', textDecoration: 'none', letterSpacing: 1, padding: '7px 12px', border: '1px solid rgba(59,130,246,.3)', borderRadius: 3 }}>
                🔨 Крафт
              </a>
              <a href="/" style={{ fontFamily: 'Rajdhani,sans-serif', fontSize: 12, color: 'var(--muted)', textDecoration: 'none', letterSpacing: 1, padding: '7px 12px', border: '1px solid var(--border)', borderRadius: 3 }}>
                ← Арена ставок
              </a>
              <input ref={fileRef} type="file" accept=".json" style={{ display: 'none' }} onChange={handleFile} />
              <button
                onClick={() => {
                  if (uploadAuth) { fileRef.current.click(); return; }
                  const pw = window.prompt('Пароль для загрузки:');
                  if (pw === process.env.REACT_APP_ADMIN_PASSWORD && process.env.REACT_APP_ADMIN_PASSWORD) {
                    setUploadAuth(true);
                    fileRef.current.click();
                  } else if (pw !== null) {
                    showMsg('✗ Неверный пароль', false);
                  }
                }}
                disabled={uploading}
                style={{ padding: '7px 16px', background: 'rgba(59,130,246,.15)', border: '1px solid rgba(59,130,246,.4)', color: 'var(--blue2)', fontFamily: 'Rajdhani,sans-serif', fontSize: 12, fontWeight: 700, letterSpacing: 1, cursor: uploading ? 'wait' : 'pointer', borderRadius: 3, textTransform: 'uppercase' }}
              >
                {uploading ? '⏳ Загрузка...' : '⬆ Загрузить JSON'}
              </button>
            </div>
          </div>

          {/* Уведомление */}
          {msg && (
            <div style={{ background: msg.ok ? 'rgba(52,211,153,.1)' : 'rgba(248,113,113,.1)', border: `1px solid ${msg.ok ? 'rgba(52,211,153,.3)' : 'rgba(248,113,113,.3)'}`, borderRadius: 3, padding: '8px 14px', color: msg.ok ? 'var(--green)' : 'var(--red)', fontFamily: 'Rajdhani,sans-serif', fontSize: 13, marginBottom: 10 }}>
              {msg.text}
            </div>
          )}

          {/* SELL / BUY / Арбитраж табы */}
          <div style={{ display: 'flex', borderBottom: '1px solid var(--border)' }}>
            <button style={tabStyle(tab === 'SELL' ? 'SELL' : false)} onClick={() => { setTab('SELL'); setExpanded(null); }}>
              📤 Продажа WTS
            </button>
            <button style={tabStyle(tab === 'BUY' ? 'BUY' : false)} onClick={() => { setTab('BUY'); setExpanded(null); }}>
              📥 Покупка WTB
            </button>
            <button
              style={{
                ...tabStyle(tab === 'ARB' ? 'ARB' : false),
                borderBottom: tab === 'ARB' ? '2px solid var(--green)' : '2px solid transparent',
                color: tab === 'ARB' ? 'var(--green)' : 'var(--muted)',
              }}
              onClick={() => { setTab('ARB'); setExpanded(null); }}
            >
              ⚡ Арбитраж
            </button>
          </div>
        </div>
      </div>

      {/* ── Фильтры ── */}
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '14px 16px 0' }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
          <input
            placeholder="🔍 Поиск по названию предмета..."
            value={search}
            onChange={e => { setSearch(e.target.value); setExpanded(null); }}
            style={{ flex: 1, minWidth: 200, padding: '8px 12px', background: 'var(--panel2)', border: '1px solid var(--border)', color: 'var(--text)', fontFamily: 'Noto Serif,serif', fontSize: 13, borderRadius: 3, outline: 'none' }}
          />
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {CITIES.map(c => {
              const clr = CITY_COLORS[c] || 'var(--blue)';
              const active = city === c;
              return (
                <button key={c} onClick={() => { setCity(c); setExpanded(null); }} style={{
                  padding: '6px 12px',
                  background: active ? clr + '22' : 'var(--panel2)',
                  border: `1px solid ${active ? clr : 'var(--border)'}`,
                  color: active ? clr : 'var(--muted)',
                  fontFamily: 'Rajdhani,sans-serif', fontSize: 12, fontWeight: 700,
                  borderRadius: 3, cursor: 'pointer', letterSpacing: 1, transition: 'all .15s',
                }}>{c}</button>
              );
            })}
          </div>
          {/* Выбор даты сканирования */}
          {scanDates.length > 0 && (
            <select
              value={scanDate}
              onChange={e => { setScanDate(e.target.value); setExpanded(null); }}
              style={{
                padding: '6px 12px',
                background: 'var(--panel2)',
                border: '1px solid var(--border)',
                color: 'var(--text)',
                fontFamily: 'Rajdhani,sans-serif', fontSize: 12, fontWeight: 700,
                borderRadius: 3, cursor: 'pointer', letterSpacing: 1,
                outline: 'none',
              }}
            >
              <option value="latest">📅 Последний скан</option>
              {scanDates.map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          )}
          {/* Мин. спред — только на вкладке арбитража; фильтр по городу уже есть выше */}
          {tab === 'ARB' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'Rajdhani,sans-serif', fontSize: 12, color: 'var(--muted)', letterSpacing: 1 }}>
              Мин. спред ₳
              <input
                type="number" min="0" step="1000"
                value={minSpread || ''}
                placeholder="0"
                onChange={e => setMinSpread(Math.max(0, Number(e.target.value) || 0))}
                style={{
                  width: 90, padding: '6px 10px',
                  background: 'var(--panel2)', border: '1px solid var(--border)',
                  color: 'var(--text)', fontFamily: 'JetBrains Mono,monospace', fontSize: 12,
                  borderRadius: 3, outline: 'none',
                }}
              />
            </label>
          )}
        </div>

        {/* Статы */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
          {(tab === 'ARB'
            ? [
                ['Сделок',   arbDeals.length],
                ['Продавцов', arbDeals.reduce((s, d) => s + d.sellers.length, 0)],
                ['Покупателей', arbDeals.reduce((s, d) => s + d.buyers.length, 0)],
              ]
            : [
                ['Предметов',   groups.length],
                ['Предложений', groups.reduce((s, g) => s + g.entries.length, 0)],
                ['Трейдеров',   new Set(groups.flatMap(g => g.entries.map(e => e.trader).filter(Boolean))).size],
              ]
          ).map(([lbl, val]) => (
            <div key={lbl} style={{ background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: 3, padding: '6px 16px', textAlign: 'center' }}>
              <div style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 16, color: tab === 'ARB' ? 'var(--green)' : 'var(--blue2)', fontWeight: 700 }}>{val}</div>
              <div style={{ fontFamily: 'Rajdhani,sans-serif', fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1 }}>{lbl}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Таблица ── */}
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 16px 40px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', color: 'var(--muted)', fontFamily: 'Rajdhani,sans-serif', padding: 60, fontSize: 16 }}>Загрузка...</div>
        ) : tab === 'ARB' ? (arbDeals.length === 0 ? (
          <div style={{ textAlign: 'center', color: 'var(--muted)', fontFamily: 'Rajdhani,sans-serif', padding: 60, fontSize: 16 }}>
            Нет сделок, где покупка дороже продажи
          </div>
        ) : (
          <ArbList deals={arbDeals} expanded={expanded} toggle={toggle} />
        )) : groups.length === 0 ? (
          <div style={{ textAlign: 'center', color: 'var(--muted)', fontFamily: 'Rajdhani,sans-serif', padding: 60, fontSize: 16 }}>Нет данных</div>
        ) : (
          <div className="pane" style={{ padding: 0, overflow: 'hidden' }}>

            {/* Шапка таблицы — кликабельная */}
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 100px 150px 70px 80px',
              padding: '9px 16px', background: 'var(--panel2)',
              borderBottom: '1px solid var(--border)',
              fontFamily: 'Rajdhani,sans-serif', fontSize: 11,
              letterSpacing: 1,
            }}>
              <SortTh id="item_name"   sort={groupSort}>Предмет</SortTh>
              <SortTh id="total_count" sort={groupSort} style={{ textAlign: 'right' }}>{mode === 'SELL' ? 'Всего шт.' : ''}</SortTh>
              <SortTh id="best_price"  sort={groupSort} style={{ textAlign: 'right' }}>{mode === 'SELL' ? 'Мин. цена' : 'Макс. цена'}</SortTh>
              <SortTh id="pct"         sort={groupSort} style={{ textAlign: 'right' }}>24ч</SortTh>
              <SortTh id="offers"      sort={groupSort} style={{ textAlign: 'center' }}>Офферов</SortTh>
            </div>

            {groups.map((g, gi) => {
              const isOpen = expanded === g.item_name;
              const sortedEntries = entrySort.apply(g.entries, {
                price:      e => e.price,
                count:      e => e.count,
                trader:     e => e.trader,
                city:       e => e.city,
                scanned_at: e => e.scanned_at,
              });

              return (
                <div key={g.item_name}>
                  {/* Строка группы */}
                  <div
                    onClick={() => toggle(g.item_name)}
                    style={{
                      display: 'grid', gridTemplateColumns: '1fr 100px 150px 70px 80px',
                      padding: '11px 16px',
                      borderBottom: `1px solid ${isOpen ? 'var(--border2)' : 'var(--border)'}`,
                      cursor: 'pointer', alignItems: 'center',
                      background: isOpen
                        ? 'rgba(59,130,246,.06)'
                        : gi % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.01)',
                      transition: 'background .1s',
                    }}
                  >
                    {/* Название */}
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{
                        color: 'var(--muted)', fontSize: 9,
                        display: 'inline-block',
                        transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                        transition: 'transform .15s',
                      }}>▶</span>
                      <span style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 600, color: 'var(--text)', fontSize: 14 }}>
                        {g.item_name}
                      </span>
                    </span>

                    {/* Кол-во */}
                    <span style={{ textAlign: 'right', fontFamily: 'JetBrains Mono,monospace', fontSize: 12, color: 'var(--text2)' }}>
                      {mode === 'SELL' ? fmt(g.total_count) : ''}
                    </span>

                    {/* Цена */}
                    <span style={{ textAlign: 'right', fontFamily: 'JetBrains Mono,monospace', fontSize: 14, fontWeight: 700, color: priceColor }}>
                      {g.best_price ? fmt(g.best_price) + ' ₳' : '—'}
                    </span>

                    {/* % изменения */}
                    <span style={{ textAlign: 'right', fontFamily: 'JetBrains Mono,monospace', fontSize: 11 }}>
                      {g.pct === null ? (
                        <span style={{ color: 'var(--muted)' }}>—</span>
                      ) : (
                        <span style={{ color: g.pct > 0 ? 'var(--red)' : g.pct < 0 ? 'var(--green)' : 'var(--muted)' }}>
                          {g.pct > 0 ? '+' : ''}{g.pct.toFixed(1)}%
                        </span>
                      )}
                    </span>

                    {/* Офферов */}
                    <span style={{ textAlign: 'center', fontFamily: 'JetBrains Mono,monospace', fontSize: 12, color: 'var(--muted)' }}>
                      {g.entries.length}
                    </span>
                  </div>

                  {/* Accordion */}
                  {isOpen && (
                    <div style={{ background: 'rgba(0,0,0,.25)', borderBottom: '2px solid var(--border2)' }}>

                      {/* График */}
                      <div style={{ padding: '14px 16px 8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                          <span style={{ fontFamily: 'Rajdhani,sans-serif', fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1 }}>
                            📈 История цен
                          </span>
                          <div style={{ display: 'flex', gap: 4 }}>
                            {RANGE_OPTIONS.map(({ value, label }) => (
                              <button key={label} onClick={e => { e.stopPropagation(); setChartRange(value); }} style={{
                                padding: '3px 8px',
                                background: chartRange === value ? 'rgba(59,130,246,.2)' : 'transparent',
                                border: `1px solid ${chartRange === value ? 'rgba(59,130,246,.5)' : 'var(--border)'}`,
                                color: chartRange === value ? 'var(--blue2)' : 'var(--muted)',
                                fontFamily: 'JetBrains Mono,monospace', fontSize: 10,
                                borderRadius: 2, cursor: 'pointer',
                              }}>{label}</button>
                            ))}
                          </div>
                        </div>
                        <PriceChart itemName={g.item_name} range={chartRange} />
                      </div>

                      {/* Список офферов */}
                      <div style={{ borderTop: '1px solid var(--border)' }}>
                        {/* Заголовок деталей — кликабельный */}
                        <div style={{
                          display: 'grid', gridTemplateColumns: '1fr 90px 130px 80px 1fr',
                          padding: '7px 16px 7px 36px',
                          fontFamily: 'Rajdhani,sans-serif', fontSize: 10,
                          letterSpacing: 1,
                          borderBottom: '1px solid var(--border)',
                        }}>
                          <SortTh id="trader"     sort={entrySort}>Трейдер</SortTh>
                          <SortTh id="city"       sort={entrySort} style={{ textAlign: 'center' }}>Город</SortTh>
                          <SortTh id="price"      sort={entrySort} style={{ textAlign: 'right' }}>Цена</SortTh>
                          <SortTh id="count"      sort={entrySort} style={{ textAlign: 'right' }}>{mode === 'SELL' ? 'Кол-во' : ''}</SortTh>
                          <SortTh id="scanned_at" sort={entrySort} style={{ textAlign: 'right' }}>Время скана</SortTh>
                        </div>

                        {sortedEntries.map((en, ei) => (
                          <div key={en.id || ei} style={{
                            display: 'grid', gridTemplateColumns: '1fr 90px 130px 80px 1fr',
                            padding: '8px 16px 8px 36px',
                            borderBottom: ei < sortedEntries.length - 1 ? '1px solid rgba(255,255,255,.03)' : 'none',
                            alignItems: 'center',
                          }}>
                            <span style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 600, color: 'var(--blue2)', fontSize: 13 }}>
                              {en.trader || '—'}
                            </span>
                            <span style={{ textAlign: 'center' }}>
                              <CityTag city={en.city} />
                            </span>
                            <span style={{ textAlign: 'right', fontFamily: 'JetBrains Mono,monospace', fontSize: 13, fontWeight: 600, color: priceColor }}>
                              {en.price ? fmt(en.price) + ' ₳' : '—'}
                            </span>
                            <span style={{ textAlign: 'right', fontFamily: 'JetBrains Mono,monospace', fontSize: 12, color: 'var(--text2)' }}>
                              {mode === 'SELL' ? fmt(en.count) : ''}
                            </span>
                            <span style={{ textAlign: 'right', fontFamily: 'JetBrains Mono,monospace', fontSize: 11, color: 'var(--muted)' }}>
                              {en.scanned_at || '—'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Арбитраж: продавцы дешевле покупателей ────────────────────────────────────
const ARB_GRID      = '1fr 90px 120px 80px 110px';
const ARB_GRID_OPEN = '1fr 90px 120px';

function ArbSection({ title, color, entries, cols, showProfit }) {
  if (!entries.length) return null;
  return (
    <div style={{ borderTop: '1px solid var(--border)' }}>
      <div style={{
        padding: '7px 16px 7px 36px',
        fontFamily: 'Rajdhani,sans-serif', fontSize: 10,
        letterSpacing: 1, textTransform: 'uppercase',
        color: 'var(--muted)',
        borderBottom: '1px solid var(--border)',
      }}>{title}</div>
      <div style={{
        display: 'grid', gridTemplateColumns: ARB_GRID_OPEN,
        padding: '6px 16px 6px 36px',
        fontFamily: 'Rajdhani,sans-serif', fontSize: 10,
        letterSpacing: 1, color: 'var(--muted)',
        borderBottom: '1px solid var(--border)',
      }}>
        {cols.map(c => <span key={c} style={{ textAlign: c === 'Трейдер' ? 'left' : c === 'Город' ? 'center' : 'right' }}>{c}</span>)}
      </div>
      {entries.map((en, ei) => (
        <div key={en.id || ei} style={{
          display: 'grid', gridTemplateColumns: ARB_GRID_OPEN,
          padding: '8px 16px 8px 36px',
          borderBottom: ei < entries.length - 1 ? '1px solid rgba(255,255,255,.03)' : 'none',
          alignItems: 'center',
        }}>
          <span style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 600, color: 'var(--blue2)', fontSize: 13 }}>
            {en.trader || '—'}
          </span>
          <span style={{ textAlign: 'center' }}>
            <CityTag city={en.city} />
          </span>
          <span style={{ display: 'flex', gap: 12, justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 13, fontWeight: 600, color }}>
              {en.price ? fmt(en.price) + ' ₳' : '—'}
            </span>
            {showProfit && en.profit > 0 && (
              <span style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 11, color: 'var(--green)', textAlign: 'right' }}>
                +{fmt(en.profit)} <span style={{ color: 'var(--muted)', fontSize: 9 }}>₳</span>
              </span>
            )}
          </span>
        </div>
      ))}
    </div>
  );
}

function ArbList({ deals, expanded, toggle }) {
  return (
    <div className="pane" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{
        display: 'grid', gridTemplateColumns: ARB_GRID,
        padding: '9px 16px', background: 'var(--panel2)',
        borderBottom: '1px solid var(--border)',
        fontFamily: 'Rajdhani,sans-serif', fontSize: 11, letterSpacing: 1, color: 'var(--muted)',
      }}>
        <span>Товар</span>
        <span style={{ textAlign: 'center' }}>Продавцов / Покупателей</span>
        <span style={{ textAlign: 'right' }}>Лучшая цена</span>
        <span style={{ textAlign: 'right' }}>Спред</span>
        <span style={{ textAlign: 'right' }}>Выгода</span>
      </div>

      {deals.map((d, di) => {
        const isOpen = expanded === d.item_name;
        return (
          <div key={d.item_name}>
            <div
              onClick={() => toggle(d.item_name)}
              style={{
                display: 'grid', gridTemplateColumns: ARB_GRID,
                padding: '11px 16px',
                borderBottom: `1px solid ${isOpen ? 'var(--border2)' : 'var(--border)'}`,
                cursor: 'pointer', alignItems: 'center',
                background: isOpen
                  ? 'rgba(52,211,153,.06)'
                  : di % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.01)',
                transition: 'background .1s',
              }}
            >
              <span style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 600, color: 'var(--text)', fontSize: 14 }}>
                {d.item_name}
              </span>
              <span style={{ textAlign: 'center', fontFamily: 'JetBrains Mono,monospace', fontSize: 12, color: 'var(--muted)' }}>
                {d.sellers.length} / {d.buyers.length}
              </span>
              <span style={{ textAlign: 'right', fontFamily: 'JetBrains Mono,monospace', fontSize: 12, color: 'var(--muted)' }}>
                {fmt(d.sellers[0]?.price)} → {fmt(d.buyers[0]?.price)}
              </span>
              <span style={{ textAlign: 'right', fontFamily: 'JetBrains Mono,monospace', fontSize: 13, fontWeight: 700, color: 'var(--green)' }}>
                +{fmt(d.spread)}
              </span>
              <span style={{ textAlign: 'right', fontFamily: 'JetBrains Mono,monospace', fontSize: 11, color: 'var(--green)' }}>
                +{d.spreadPct.toFixed(1)}%
              </span>
            </div>

            {isOpen && (
              <div style={{ background: 'rgba(0,0,0,.15)', borderBottom: '2px solid var(--border2)' }}>
                <div style={{ padding: '12px 16px 4px' }}>
                  <span style={{ fontFamily: 'Rajdhani,sans-serif', fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1 }}>
                    📈 История спреда
                  </span>
                  <SpreadChart itemName={d.item_name} />
                </div>
                <ArbSection
                  title="Продавцы (WTS — от дешёвого)"
                  color={COLOR_SELL}
                  entries={d.sellers}
                  cols={['Трейдер', 'Город', 'Цена']}
                  showProfit
                />
                <ArbSection
                  title="Покупатели (WTB — от дорогого)"
                  color={COLOR_BUY}
                  entries={d.buyers}
                  cols={['Трейдер', 'Город', 'Цена']}
                  showProfit={false}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
